// ============================================================
// TMR — por que um link mágico não virou sessão
// ============================================================

/**
 * Erro de auth com `status` numérico, no mesmo formato que o `describeAuthError` do login-kit mapeia.
 *
 * Duplicado deliberadamente do `clerk.tsx`: este módulo é pura leitura de URL e não importa o Clerk, que é
 * o que permite testá-lo sem montar um provider inteiro.
 */
export class EmailLinkError extends Error {
  readonly status = 401;

  constructor(code: string) {
    super(code);
    this.name = 'EmailLinkError';
  }
}

/**
 * Traduz o desfecho de uma verificação de link mágico no código que a tela sabe explicar.
 *
 * `client_mismatch` é o caso que aparece de verdade, e o mais fácil de confundir com "não funcionou": o
 * Clerk exige, por padrão, que o link seja aberto no mesmo navegador que o pediu, e quem clica pelo
 * webmail no celular cai exatamente aí. É configuração — "Require the same device and browser" — e não
 * defeito; mas a pessoa só descobre isso se alguém disser.
 *
 * @param verificationStatus O status da verificação, como o Clerk o devolve.
 */
export function emailLinkFailure(verificationStatus: string | null | undefined): Error {
  if (verificationStatus === 'client_mismatch') return new EmailLinkError('magic_link_client_mismatch');
  if (verificationStatus === 'expired') return new EmailLinkError('magic_link_expired');
  return new EmailLinkError('magic_link_failed');
}

/**
 * O que o Clerk diz na URL de chegada do link mágico — `__clerk_status` no endereço em que a pessoa
 * aterrissa depois de clicar.
 *
 * Ler isto é o que separa "abri o link, voltei para a tela de login e não sei por quê" de uma explicação.
 * O portal ignorava o parâmetro, então um link aberto no navegador errado terminava numa tela de login
 * idêntica à de sempre, sem uma palavra sobre o que houve.
 *
 * @param search A query string a ler. Default: a da página atual.
 * @returns O erro correspondente, ou null quando não há nada a dizer — visita normal, ou verificação que
 * deu certo (aí quem fala é a sessão que acabou de existir).
 */
export function emailLinkArrivalError(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): Error | null {
  const status = new URLSearchParams(search).get('__clerk_status');
  if (!status || status === 'verified') return null;

  // `failed` não diz mais do que "não deu"; mandá-lo adiante como se fosse um motivo produziria uma
  // mensagem específica e possivelmente errada. O genérico é o honesto aqui.
  return emailLinkFailure(status === 'failed' ? null : status);
}
